import { useEffect, useState } from 'react'

import type { SearchProperty } from '@/api/types/hor'

import {
  getHotelEmissionsRequestBody,
  getPropertyEmissionsRequestBody,
} from './helpers'

import {
  postHotelBatchCarbonEmission,
  postPropertyBatchCarbonEmission,
} from '../co2'

import type {
  EmissionHotelSegment,
  EmissionPropertySegment,
  EmissionsInKg,
  TransformedHotelBatchCarbonEmissionResponse,
  TransformedPropertyBatchCarbonEmissionResponse,
} from './types'

export const useGetSinglePropertyEmission = (property: SearchProperty) => {
  const variant = property?.hotelId ? 'hotel' : 'property'
  const [hotelData, setHotelData] =
    useState<TransformedHotelBatchCarbonEmissionResponse | null>(null)
  const [propertyData, setPropertyData] =
    useState<TransformedPropertyBatchCarbonEmissionResponse | null>(null)

  useEffect(() => {
    const fetchEcoStats = async () => {
      if (property) {
        if (variant === 'hotel') {
          postHotelBatchCarbonEmission(
            getHotelEmissionsRequestBody({ properties: [property] })
          ).then((data) => {
            setHotelData(data)
          })
        } else {
          postPropertyBatchCarbonEmission(
            getPropertyEmissionsRequestBody({ properties: [property] })
          ).then((data) => {
            setPropertyData(data)
          })
        }
      }
    }
    fetchEcoStats()
  }, [property])

  return variant === 'hotel' ? hotelData : propertyData
}

export const useGetMultiplePropertiesEmissions = (
  properties: SearchProperty[]
) => {
  const [hotelData, setHotelData] = useState<{
    [key: string | number]: TransformedHotelBatchCarbonEmissionResponse
  }>({})
  const [propertyData, setPropertyData] = useState<{
    [key: string | number]: TransformedPropertyBatchCarbonEmissionResponse
  }>({})

  useEffect(() => {
    if (properties?.length === 0) {
      return
    }

    const hotelList = properties?.filter((property) => property.hotelId)
    const propertyList = properties?.filter((property) => !property.hotelId)

    hotelList?.forEach((hotel) => {
      if (hotel.hotelId in hotelData || !hotel) {
        return
      }
      postHotelBatchCarbonEmission(
        getHotelEmissionsRequestBody({ properties: [hotel] })
      ).then((data) => {
        setHotelData((prev) => ({ ...prev, [hotel.hotelId]: data }))
      })
    })

    propertyList?.forEach((property) => {
      if (property.id in propertyData || !property) {
        return
      }
      postPropertyBatchCarbonEmission(
        getPropertyEmissionsRequestBody({ properties: [property] })
      ).then((data) => {
        setPropertyData((prev) => ({ ...prev, [property.id]: data }))
      })
    })
  }, [properties])

  const data = {
    ...Object.entries(hotelData)?.reduce(
      (acc, [id, { segments }]) => ({ ...acc, [id]: segments?.[0] }),
      {}
    ),
    ...Object.entries(propertyData)?.reduce(
      (acc, [id, { segments }]) => ({ ...acc, [id]: segments?.[0] }),
      {}
    ),
  }

  return data
}

export const useGetPropertiesEmissions = (
  properties: SearchProperty[] = []
): {
  data?: {
    [id: string]: (EmissionHotelSegment | EmissionPropertySegment) &
      EmissionsInKg
  }
  isLoading?: boolean
  isError?: boolean
  error?: unknown
} => {
  const [hotelData, setHotelData] =
    useState<TransformedHotelBatchCarbonEmissionResponse | null>(null)
  const [propertyData, setPropertyData] =
    useState<TransformedPropertyBatchCarbonEmissionResponse | null>(null)

  useEffect(() => {
    if (properties?.length === 0) {
      return
    }

    const hotelList = properties.filter((property) => property.hotelId)
    const propertyList = properties.filter((property) => !property.hotelId)

    if (hotelList.length > 0) {
      postHotelBatchCarbonEmission(
        getHotelEmissionsRequestBody({ properties: hotelList })
      ).then((data) => {
        setHotelData(data)
      })
    }

    if (propertyList.length > 0) {
      postPropertyBatchCarbonEmission(
        getPropertyEmissionsRequestBody({ properties: propertyList })
      ).then((data) => {
        setPropertyData(data)
      })
    }
  }, [properties])

  const data = {
    ...hotelData?.segments?.reduce(
      (acc, hotel) => ({ ...acc, [hotel.hotel_id]: hotel }),
      {}
    ),
    ...propertyData?.segments?.reduce(
      (acc, property) => ({ ...acc, [property.property_id]: property }),
      {}
    ),
  }

  return data
}
